import { useQuery, useQueries } from '@tanstack/react-query';
import { apiClient } from '../client/http-client';
import { queryKeys } from '../utils/query-keys';

/**
 * Hook to fetch interaction status (like/bookmark) for a single recipe
 * Uses Redis cache on backend with graceful database fallback
 * 
 * Note: This hook fetches the current like/bookmark status from Redis.
 * For mutation hooks (like, unlike, bookmark, unbookmark), use useRecipeInteractions from './recipes'
 */
export function useRecipeInteractionStatus(recipeId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.recipes.interactions(recipeId),
    queryFn: () => apiClient.getRecipeInteractions(recipeId),
    enabled: enabled && !!recipeId,
    staleTime: 5 * 60 * 1000, // 5 minutes - interactions don't change frequently
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to fetch interaction status for multiple recipes
 * Optimized for list views - fetches all at once
 */
export function useBulkRecipeInteractions(
  recipeIds: string[],
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.recipes.bulkInteractions(recipeIds),
    queryFn: () => apiClient.getBulkRecipeInteractions(recipeIds),
    enabled: enabled && recipeIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to fetch interaction status for multiple recipes individually
 * Falls back to individual queries if bulk endpoint is not available
 */
export function useMultipleRecipeInteractionStatuses(
  recipeIds: string[],
  enabled: boolean = true
) {
  return useQueries({
    queries: recipeIds.map((recipeId) => ({
      queryKey: queryKeys.recipes.interactions(recipeId),
      queryFn: () => apiClient.getRecipeInteractions(recipeId),
      enabled: enabled && !!recipeId,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 2,
    })),
  });
}
